// Browser OS detection — pure + testable. Prefers the modern, high-entropy
// userAgentData.platform hint and falls back to parsing the legacy userAgent
// string. Used to default-select the matching tab in the host install block;
// the user can always override (a Mac operator often provisions a Windows host).

export type DetectedOs = "windows" | "macos" | "linux" | "unknown";

// Narrow shape of the bits of navigator we read, so the helper stays pure and
// unit-testable without a real browser global.
export interface OsSignals {
  platform?: string | null;
  userAgent?: string | null;
}

// Classify from raw signals. Order matters: check platform hint first (it is the
// most reliable when present), then fall back to userAgent substrings.
export function detectOsFrom(signals: OsSignals): DetectedOs {
  const platform = (signals.platform ?? "").toLowerCase();
  const ua = (signals.userAgent ?? "").toLowerCase();

  const hay = platform || ua;
  if (!hay) return "unknown";

  // iOS/Android are not host-install targets; fold them into unknown so we do
  // not mislead with a Linux/macOS default on a phone.
  if (/android/.test(ua)) return "unknown";
  if (/iphone|ipad|ipod/.test(ua) || /^ip(hone|ad|od)/.test(platform)) return "unknown";

  if (/win/.test(hay)) return "windows";
  if (/mac/.test(hay)) return "macos";
  if (/linux|x11|cros/.test(hay)) return "linux";

  return "unknown";
}

// Live read of the current browser's signals. Guarded for SSR / non-browser.
export function detectOs(): DetectedOs {
  if (typeof navigator === "undefined") return "unknown";
  const nav = navigator as Navigator & {
    userAgentData?: { platform?: string };
  };
  return detectOsFrom({
    platform: nav.userAgentData?.platform ?? null,
    userAgent: nav.userAgent ?? null,
  });
}
